/**
 * WorkerManagementPage — Generic worker/staff management
 *
 * Used by: CFSS, ALF, PCA, CDCS, etc.
 * Industry config comes from constellation metadata.
 *
 * CFSS: Participant employs workers (EMPLOYS edge)
 * ALF: Facility employs staff (EMPLOYS edge)
 *
 * Same pattern, different labels from settings.
 *
 * Route: /{industry}/workers
 * Scope: worker:manage
 */

import React, { useState, useCallback } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconCheck,
  IconPlus,
  IconSearch,
  IconUser,
  IconUserMinus,
  IconUserPlus,
  IconUsers,
  IconAlertCircle,
  IconPhone,
  IconMail,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useHasScope } from '@/auth';

const GET_MY_WORKERS = gql`
  query GetMyWorkers($edgeType: String!, $limit: Int) {
    mySubjectEdges(edgeType: $edgeType, limit: $limit) {
      id
      sourceActorId
      targetActorId
      edgeType
      metadata
      createdAt
    }
  }
`;

const GET_AVAILABLE_WORKERS = gql`
  query GetAvailableWorkers($roleId: String, $limit: Int) {
    actors(roleId: $roleId, limit: $limit) {
      id
      displayName
      email
      phone
      status
    }
  }
`;

const ADD_WORKER = gql`
  mutation AddScopeEdge($input: AddScopeEdgeInput!) {
    addScopeEdge(input: $input) {
      id
      edgeType
      sourceActorId
      targetActorId
    }
  }
`;

const REMOVE_WORKER = gql`
  mutation RemoveScopeEdge($input: RemoveScopeEdgeInput!) {
    removeScopeEdge(input: $input) {
      success
    }
  }
`;

interface WorkerEdge {
  id: string;
  sourceActorId: string;
  targetActorId: string;
  edgeType: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

interface Actor {
  id: string;
  displayName: string;
  email: string | null;
  phone: string | null;
  status: string;
}

interface WorkerManagementPageProps {
  title?: string;
  subtitle?: string;
  workerLabel?: string;
  workerRoleId?: string;
  edgeType?: string;
  scope?: string;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export function WorkerManagementPage({
  title = 'Staff Management',
  subtitle = 'Manage your team members',
  workerLabel = 'Staff',
  workerRoleId = 'worker',
  edgeType = 'EMPLOYS',
  scope = 'worker:manage',
}: WorkerManagementPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [hireModalOpen, setHireModalOpen] = useState(false);
  const [terminateModalOpen, setTerminateModalOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState<WorkerEdge | null>(null);
  const [terminationReason, setTerminationReason] = useState('');

  const canManage = useHasScope(scope);

  const { data: workersData, loading: workersLoading, refetch: refetchWorkers } = useQuery<{
    mySubjectEdges: WorkerEdge[];
  }>(GET_MY_WORKERS, {
    variables: { edgeType, limit: 50 },
  });

  const { data: availableData, loading: availableLoading } = useQuery<{
    actors: Actor[];
  }>(GET_AVAILABLE_WORKERS, {
    variables: { roleId: workerRoleId, limit: 100 },
    skip: !hireModalOpen,
  });

  const [addWorker, { loading: adding }] = useMutation(ADD_WORKER);
  const [removeWorker, { loading: removing }] = useMutation(REMOVE_WORKER);

  const currentWorkers = workersData?.mySubjectEdges ?? [];
  const availableWorkers = availableData?.actors ?? [];

  const hiredIds = new Set(currentWorkers.map(w => w.targetActorId));
  const filteredAvailable = availableWorkers.filter(w => {
    if (hiredIds.has(w.id)) return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      w.displayName?.toLowerCase().includes(query) ||
      w.email?.toLowerCase().includes(query)
    );
  });

  const handleHireWorker = useCallback(async (workerId: string) => {
    try {
      await addWorker({
        variables: {
          input: {
            edgeType,
            targetActorId: workerId,
            metadata: { hiredAt: new Date().toISOString(), status: 'active' },
          },
        },
      });
      notifications.show({
        title: `${workerLabel} Added`,
        message: `The ${workerLabel.toLowerCase()} has been added to your team.`,
        color: 'green',
      });
      setHireModalOpen(false);
      refetchWorkers();
    } catch (err) {
      notifications.show({
        title: 'Action Failed',
        message: err instanceof Error ? err.message : 'An error occurred.',
        color: 'red',
      });
    }
  }, [addWorker, edgeType, workerLabel, refetchWorkers]);

  const handleOpenTerminate = useCallback((worker: WorkerEdge) => {
    setSelectedWorker(worker);
    setTerminationReason('');
    setTerminateModalOpen(true);
  }, []);

  const handleConfirmTerminate = useCallback(async () => {
    if (!selectedWorker) return;
    try {
      await removeWorker({
        variables: {
          input: {
            edgeId: selectedWorker.id,
            reason: terminationReason || 'Voluntary separation',
          },
        },
      });
      notifications.show({
        title: `${workerLabel} Removed`,
        message: `The ${workerLabel.toLowerCase()} has been removed from your team.`,
        color: 'orange',
      });
      setTerminateModalOpen(false);
      setSelectedWorker(null);
      refetchWorkers();
    } catch (err) {
      notifications.show({
        title: 'Removal Failed',
        message: err instanceof Error ? err.message : 'An error occurred.',
        color: 'red',
      });
    }
  }, [selectedWorker, terminationReason, removeWorker, workerLabel, refetchWorkers]);

  if (workersLoading) {
    return (
      <Center h="60vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!canManage) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" title="Access Denied" icon={<IconAlertCircle />}>
          You do not have permission to manage {workerLabel.toLowerCase()}s.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <Group gap="xs">
            <ThemeIcon size="lg" variant="light" color="blue">
              <IconUsers size={20} />
            </ThemeIcon>
            <Stack gap={0}>
              <Title order={2}>{title}</Title>
              <Text size="sm" c="dimmed">{subtitle}</Text>
            </Stack>
          </Group>
          <Button
            leftSection={<IconUserPlus size={16} />}
            onClick={() => setHireModalOpen(true)}
          >
            Add {workerLabel}
          </Button>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Active {workerLabel}s</Text>
              <ThemeIcon size="sm" variant="light" color="green">
                <IconCheck size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c="green">{currentWorkers.length}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">This Month</Text>
              <ThemeIcon size="sm" variant="light" color="blue">
                <IconUserPlus size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>
              {currentWorkers.filter(w => {
                const created = new Date(w.createdAt);
                const now = new Date();
                return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
              }).length}
            </Text>
            <Text size="xs" c="dimmed">New additions</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Available</Text>
              <ThemeIcon size="sm" variant="light" color="gray">
                <IconUser size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{Math.max(0, availableWorkers.length - currentWorkers.length)}</Text>
            <Text size="xs" c="dimmed">In registry</Text>
          </Card>
        </SimpleGrid>

        {currentWorkers.length === 0 ? (
          <Alert color="blue" title={`No ${workerLabel}s Yet`} icon={<IconUsers />}>
            Click "Add {workerLabel}" to add someone to your team.
          </Alert>
        ) : (
          <Paper withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{workerLabel}</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Added</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {currentWorkers.map(worker => (
                  <Table.Tr key={worker.id}>
                    <Table.Td>
                      <Group gap="xs">
                        <ThemeIcon size="sm" variant="light" color="blue">
                          <IconUser size={12} />
                        </ThemeIcon>
                        <Text size="sm" fw={500}>{worker.targetActorId}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge color="green" size="sm">Active</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{formatDate(worker.createdAt)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Button
                        size="xs"
                        variant="light"
                        color="red"
                        leftSection={<IconUserMinus size={14} />}
                        onClick={() => handleOpenTerminate(worker)}
                      >
                        Remove
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>

      <Modal opened={hireModalOpen} onClose={() => setHireModalOpen(false)} title={`Add ${workerLabel}`} size="lg">
        <Stack gap="md">
          <TextInput
            placeholder="Search by name or email..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={e => setSearchQuery(e.currentTarget.value)}
          />
          {availableLoading ? (
            <Center py="xl"><Loader size="sm" /></Center>
          ) : filteredAvailable.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="md">No available {workerLabel.toLowerCase()}s found</Text>
          ) : (
            <Stack gap="xs">
              {filteredAvailable.slice(0, 10).map(worker => (
                <Paper key={worker.id} withBorder p="sm">
                  <Group justify="space-between">
                    <Stack gap={2}>
                      <Text size="sm" fw={500}>{worker.displayName}</Text>
                      <Group gap="xs">
                        {worker.email && <Group gap={4}><IconMail size={12} /><Text size="xs" c="dimmed">{worker.email}</Text></Group>}
                        {worker.phone && <Group gap={4}><IconPhone size={12} /><Text size="xs" c="dimmed">{worker.phone}</Text></Group>}
                      </Group>
                    </Stack>
                    <Button size="xs" leftSection={<IconUserPlus size={14} />} loading={adding} onClick={() => handleHireWorker(worker.id)}>
                      Add
                    </Button>
                  </Group>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      </Modal>

      <Modal opened={terminateModalOpen} onClose={() => setTerminateModalOpen(false)} title={`Remove ${workerLabel}`} centered size="sm">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Removing this {workerLabel.toLowerCase()} will end their assignment.
          </Text>
          <Textarea
            label="Reason (optional)"
            placeholder="e.g., Voluntary resignation, End of assignment..."
            minRows={2}
            value={terminationReason}
            onChange={e => setTerminationReason(e.currentTarget.value)}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setTerminateModalOpen(false)} disabled={removing}>Cancel</Button>
            <Button color="red" onClick={handleConfirmTerminate} loading={removing}>Remove {workerLabel}</Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default WorkerManagementPage;
